import { useEffect, useState } from "react";
import axios, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { BookMarked, Loader2, Plus, Folder } from "lucide-react";

/**
 * SaveToPortfolioDialog — pick an existing portfolio or create a new one,
 * then save the current render into it (with optional notes).
 */
export default function SaveToPortfolioDialog({ open, onOpenChange, source, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [portfolios, setPortfolios] = useState([]);
  const [mode, setMode] = useState("pick"); // "pick" | "new"
  const [selectedId, setSelectedId] = useState("");
  const [name, setName] = useState("");
  const [prefix, setPrefix] = useState("");
  const [atelier, setAtelier] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) {
      setMode("pick"); setSelectedId(""); setName(""); setPrefix(""); setAtelier(""); setNotes("");
      return;
    }
    setLoading(true);
    axios.get(`${API}/studio/portfolios`)
      .then((r) => {
        const list = r.data?.portfolios || [];
        setPortfolios(list);
        if (list.length) {
          setSelectedId(list[0].id);
        } else {
          setMode("new");
        }
      })
      .catch(() => toast.error("Could not load portfolios."))
      .finally(() => setLoading(false));
  }, [open]);

  const submit = async () => {
    if (!source?.id) return;
    setSubmitting(true);
    try {
      let portfolioId = selectedId;
      if (mode === "new") {
        if (!name.trim()) {
          toast.error("Portfolio name is required.");
          setSubmitting(false);
          return;
        }
        const r = await axios.post(`${API}/studio/portfolios`, {
          name: name.trim(),
          prefix: prefix.trim() || undefined,
          atelier_name: atelier.trim() || undefined,
        });
        portfolioId = r.data.id;
      }
      const res = await axios.post(`${API}/studio/portfolios/${portfolioId}/items`, {
        generation_id: source.id,
        notes: notes.trim() || undefined,
      });
      toast.success(`Saved as ${res.data.design_number}`);
      onSaved?.(res.data.item, portfolioId);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Save failed.";
      toast.error(String(detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="save-portfolio-dialog"
        className="bg-[#0E0E0E] border border-white/10 text-white p-0 rounded-2xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)] w-[94vw] max-w-[520px] sm:!rounded-2xl"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/8 space-y-1">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
            <BookMarked className="w-3 h-3" /> Portfolio · Save piece
          </div>
          <DialogTitle className="font-display text-lg md:text-xl tracking-tight text-white">
            Save render to a portfolio
          </DialogTitle>
          <DialogDescription className="text-white/50 text-[11px] leading-snug">
            A design number is generated automatically. The current BOM is snapshotted with the piece.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-4 space-y-3.5">
          {/* Mode toggle */}
          <div className="inline-flex rounded-full border border-white/10 p-0.5 text-[11px]">
            <button
              type="button"
              onClick={() => setMode("pick")}
              data-testid="save-portfolio-pick-tab"
              disabled={portfolios.length === 0}
              className={`px-3 py-1 rounded-full transition disabled:opacity-30 ${
                mode === "pick" ? "bg-[#D4AF37] text-black" : "text-white/70 hover:text-white"
              }`}
            >
              Existing
            </button>
            <button
              type="button"
              onClick={() => setMode("new")}
              data-testid="save-portfolio-new-tab"
              className={`px-3 py-1 rounded-full transition ${
                mode === "new" ? "bg-[#D4AF37] text-black" : "text-white/70 hover:text-white"
              }`}
            >
              <span className="inline-flex items-center gap-1"><Plus className="w-3 h-3" /> New</span>
            </button>
          </div>

          {mode === "pick" ? (
            <div className="space-y-2">
              {loading ? (
                <div className="rounded-lg border border-white/10 bg-[#141414] p-3 text-center text-[11px] text-white/50">
                  Loading portfolios…
                </div>
              ) : portfolios.length === 0 ? (
                <div className="rounded-lg border border-dashed border-white/15 bg-[#141414] p-4 text-center">
                  <p className="text-[12px] text-white/55">No portfolios yet — create your first one.</p>
                </div>
              ) : (
                <div className="max-h-[200px] overflow-y-auto space-y-1.5 pr-1">
                  {portfolios.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSelectedId(p.id)}
                      data-testid={`save-portfolio-option-${p.id}`}
                      className={`w-full text-left rounded-lg border p-2.5 transition flex items-center gap-2.5 ${
                        selectedId === p.id
                          ? "border-[#D4AF37] bg-[#1A1814]"
                          : "border-white/10 hover:border-white/25 bg-[#141414]"
                      }`}
                    >
                      <Folder className={`w-3.5 h-3.5 ${selectedId === p.id ? "text-[#D4AF37]" : "text-white/40"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] text-white truncate">{p.name}</div>
                        <div className="text-[10px] text-white/45">
                          {p.prefix}-### · {p.item_count} {p.item_count === 1 ? "piece" : "pieces"}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1">
                  Portfolio name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Diwali 2026 Collection"
                  data-testid="save-portfolio-name"
                  maxLength={80}
                  className="w-full bg-[#141414] border border-white/10 rounded-md px-2.5 py-2 text-[12px] text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1">
                    Design prefix
                  </label>
                  <input
                    type="text"
                    value={prefix}
                    onChange={(e) => setPrefix(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                    placeholder="DWL"
                    maxLength={8}
                    data-testid="save-portfolio-prefix"
                    className="w-full bg-[#141414] border border-white/10 rounded-md px-2.5 py-2 text-[12px] text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1">
                    Atelier name
                  </label>
                  <input
                    type="text"
                    value={atelier}
                    onChange={(e) => setAtelier(e.target.value)}
                    placeholder="(optional)"
                    maxLength={80}
                    data-testid="save-portfolio-atelier"
                    className="w-full bg-[#141414] border border-white/10 rounded-md px-2.5 py-2 text-[12px] text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
                  />
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Anything specific about this piece for the catalog?"
              maxLength={400}
              data-testid="save-portfolio-notes"
              className="w-full bg-[#141414] border border-white/10 rounded-md px-2.5 py-2 text-[12px] text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition resize-none leading-snug"
            />
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="text-xs px-4 py-2 rounded-full border border-white/15 text-white/75 hover:bg-white/5 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting || loading || (mode === "pick" && !selectedId) || (mode === "new" && !name.trim())}
              data-testid="save-portfolio-submit"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-xs font-medium py-2 hover:bg-[#F3CE5A] transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>
              ) : (
                <><BookMarked className="w-3.5 h-3.5" /> Save piece</>
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
